/**
 * Driver-self-service — Document Upload Modal (Plan §2.2 M-F-21).
 * Phase 4 G5. Consumes POST /driver/me/documents (DRIVER_OWN_DOCUMENT_UPLOAD).
 *
 * Differs from admin DocumentUploadModal: no driverId parameter, posts to
 * the driver-self endpoint. Same C-H-2 client-side pre-check.
 */
import React, { useState } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

const DOC_TYPES = [
  ['permis_de_conduire',       'Permis de conduire'],
  ['carte_grise',              'Carte grise'],
  ['attestation_assurance',    "Attestation d'assurance"],
  ['capacite_professionnelle', 'Capacité professionnelle'],
  ['kbis',                     'Extrait Kbis'],
  ['casier_judiciaire',        'Casier judiciaire'],
  ['justificatif_domicile',    'Justificatif de domicile'],
  ['identity_document',        "Pièce d'identité"],
];

const ALLOWED_MIME = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const MAX_BYTES = 20 * 1024 * 1024;

const DriverDocumentUploadModal = ({ token, theme, onClose, onSuccess }) => {
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [expiry, setExpiry] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!docType) { setError('Veuillez sélectionner un type.'); return; }
    if (!file)    { setError('Veuillez sélectionner un fichier.'); return; }
    if (!ALLOWED_MIME.has(file.type)) { setError('PDF, JPEG ou PNG uniquement.'); return; }
    if (file.size > MAX_BYTES) { setError('Fichier trop volumineux (max. 20 Mo).'); return; }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', docType);
    if (expiry) fd.append('expiry_date', new Date(expiry).toISOString());

    setLoading(true);
    try {
      await axios.post(`${API_URL}/driver/me/documents`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Idempotency-Key': `self-doc-upload-${Date.now()}`,
        },
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason) {
        setError(detail.reason);
      } else {
        setError('Échec du téléversement.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" data-testid="driver-self-upload-modal">
      <div className={`${theme.bgCard} ${theme.border} border rounded-2xl w-full max-w-md p-6 space-y-4`}>
        <div className="flex items-center justify-between">
          <h3 className={`${theme.text} text-lg font-semibold`}>Téléverser un document</h3>
          <button type="button" onClick={onClose} className={theme.textSub}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className={`${theme.textSub} block text-xs mb-1`}>Type de document</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className={`${theme.bgSubtle} ${theme.text} w-full px-3 py-2 rounded-lg border ${theme.border} text-sm`}
            >
              <option value="">—</option>
              {DOC_TYPES.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`${theme.textSub} block text-xs mb-1`}>Fichier (PDF / JPEG / PNG ≤ 20 Mo)</label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={`${theme.text} w-full text-sm`}
            />
          </div>
          <div>
            <label className={`${theme.textSub} block text-xs mb-1`}>Date d'expiration (optionnelle)</label>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className={`${theme.bgSubtle} ${theme.text} w-full px-3 py-2 rounded-lg border ${theme.border} text-sm`}
            />
          </div>
          {error && <div className="text-rose-400 text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`${theme.textSub} px-3 py-2 rounded-lg text-sm hover:bg-white/5`}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-2 rounded-lg bg-amber-500 text-black font-medium text-sm hover:bg-amber-400 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Téléverser
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverDocumentUploadModal;
